import { useState, useRef, useEffect } from 'react';
import { Paper, IconButton, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { useAuth } from '../../context/authContext';
import StarRating from './StarRating';

const API_URL = import.meta.env.VITE_API_URL;

export default function ChatbotPanel({ onClose }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [view, setView] = useState('loading');
  const [categories, setCategories] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedFaqId, setSelectedFaqId] = useState(null);
  const [ratingKey, setRatingKey] = useState(0);
  const [actionLoading, setActionLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, view]);

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const response = await fetch(`${API_URL}/chatbot/faq/categories`);
      if (!response.ok) throw new Error();
      const cats = await response.json();
      if (!user) {
        setCategories(cats.filter(cat => cat.name === 'Acceso a RDS'));
      } else {
        setCategories(cats);
      }
    } catch {
      setCategories([]);
    } finally {
      setMessages([{
        id: 'welcome',
        text: '¡Hola! Soy el asistente virtual de RDS. ¿Qué dudas tienes?',
        sender: 'bot',
      }]);
      setView('categories');
    }
  };

  const handleCategoryClick = async (category) => {
    setSelectedCategory(category);
    setActionLoading(true);
    setMessages((prev) => [
      ...prev,
      { id: Date.now().toString(), text: category, sender: 'user' },
    ]);

    try {
      const response = await fetch(
        `${API_URL}/chatbot/faq/active?category=${encodeURIComponent(category)}&platform=web`,
      );
      if (!response.ok) throw new Error();
      const faqs = await response.json();
      setQuestions(faqs);
      setMessages((prev) => [
        ...prev,
        {
          id: (Date.now() + 1).toString(),
          text: `Estas son las preguntas disponibles sobre ${category}:`,
          sender: 'info',
        },
      ]);
      setView('questions');
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          id: (Date.now() + 1).toString(),
          text: 'Ocurrió un error al cargar las preguntas. Intenta nuevamente.',
          sender: 'bot',
        },
      ]);
    } finally {
      setActionLoading(false);
    }
  };

  const handleQuestionClick = async (faq) => {
    if (faq.isOtherQuery) {
      const cat = categories.find(c => c.name === selectedCategory);
      const name = cat?.responsibleName;
      const email = cat?.responsibleEmail;
      const text = name && email
        ? `Para otras consultas sobre ${selectedCategory}, contáctate con ${name} a ${email}`
        : 'Para otras consultas, contáctate con el equipo CIDERE a contacto@cidere.cl';
      setMessages((prev) => [
        ...prev,
        { id: Date.now().toString(), text: faq.question, sender: 'user' },
        { id: (Date.now() + 1).toString(), text, sender: 'bot' },
      ]);
      setSelectedCategory(null);
      setView('categories');
      return;
    }

    setSelectedFaqId(faq.id);
    setRatingKey((k) => k + 1);
    setActionLoading(true);
    setMessages((prev) => [
      ...prev,
      { id: Date.now().toString(), text: faq.question, sender: 'user' },
    ]);

    try {
      const response = await fetch(`${API_URL}/chatbot/faq/${faq.id}`);
      if (!response.ok) throw new Error();
      const data = await response.json();
      setMessages((prev) => [
        ...prev,
        {
          id: (Date.now() + 1).toString(),
          text: data.answer,
          sender: 'bot',
        },
      ]);
      setView('answer');
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          id: (Date.now() + 1).toString(),
          text: 'Ocurrió un error al obtener la respuesta. Intenta nuevamente.',
          sender: 'bot',
        },
      ]);
    } finally {
      setActionLoading(false);
    }
  };

  const handleOtherQuestion = () => {
    setSelectedCategory(null);
    setView('categories');
  };

  const handleBackToQuestions = () => {
    setMessages((prev) => [
      ...prev,
      { id: Date.now().toString(), text: '⬅ Volver a preguntas', sender: 'user' },
    ]);
    setView('questions');
  };

  const handleBackToCategories = () => {
    setMessages((prev) => [
      ...prev,
      { id: Date.now().toString(), text: '⬅ Volver al inicio', sender: 'user' },
    ]);
    setSelectedCategory(null);
    setView('categories');
  };

  return (
    <Paper
      elevation={8}
      className="flex flex-col"
      style={{
        width: 380,
        height: 560,
        borderRadius: 16,
        overflow: 'hidden',
        position: 'fixed',
        bottom: 100,
        right: 20,
        zIndex: 1300,
      }}
    >
      <div className="flex items-center justify-between px-4 py-3" style={{ backgroundColor: '#C42251' }}>
        <div className="flex items-center gap-2">
          <SmartToyIcon style={{ color: '#fff' }} />
          <span className="text-white font-semibold text-sm">Asistente RDS</span>
        </div>
        <IconButton size="small" onClick={onClose} style={{ color: '#fff' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-3" style={{ backgroundColor: '#f5f5f5' }}>
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`flex ${msg.sender === 'user' ? 'justify-end' : msg.sender === 'info' ? 'justify-center' : 'justify-start'}`}
          >
            {msg.sender === 'info' ? (
              <div
                className="px-3 py-1.5 rounded-lg text-xs leading-relaxed"
                style={{
                  backgroundColor: '#F3F4F6',
                  color: '#6B7280',
                  fontStyle: 'italic',
                  textAlign: 'center',
                  maxWidth: '90%',
                }}
              >
                {msg.text}
              </div>
            ) : (
              <div
                className="max-w-[260px] px-3 py-2 rounded-2xl text-sm leading-relaxed"
                style={{
                  backgroundColor: msg.sender === 'user' ? '#0488AF' : '#fff',
                  color: msg.sender === 'user' ? '#fff' : '#333',
                  borderTopRightRadius: msg.sender === 'user' ? 4 : 16,
                  borderTopLeftRadius: msg.sender === 'user' ? 16 : 4,
                  boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                }}
              >
                {msg.text}
              </div>
            )}
          </div>
        ))}

        {actionLoading && (
          <div className="flex justify-start">
            <div
              className="px-3 py-2 rounded-2xl text-sm"
              style={{
                backgroundColor: '#fff',
                borderTopLeftRadius: 4,
                boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
              }}
            >
              <CircularProgress size={16} style={{ color: '#0488AF' }} />
            </div>
          </div>
        )}

        {view === 'categories' && !actionLoading && (
          <div className="flex flex-wrap gap-2">
            {categories.length === 0 ? (
              <div
                className="max-w-[260px] px-3 py-2 rounded-2xl text-sm leading-relaxed"
                style={{
                  backgroundColor: '#fff',
                  color: '#333',
                  borderTopLeftRadius: 4,
                  boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                }}
              >
                Por ahora no hay temas disponibles. Intenta más tarde.
              </div>
            ) : (
              categories.map((cat) => (
                <button
                  key={cat.name}
                  onClick={() => handleCategoryClick(cat.name)}
                  className="px-3 py-1.5 rounded-full text-sm font-medium transition-colors hover:opacity-90"
                  style={{
                    backgroundColor: '#09274A',
                    color: '#fff',
                    border: 'none',
                    cursor: 'pointer',
                  }}
                >
                  {cat.name}
                </button>
              ))
            )}
            {!user && categories.length > 0 && (
              <div className="w-full mt-2 text-xs text-gray-500 text-center italic">
                Para ver más temas, inicia sesión en tu cuenta RDS
              </div>
            )}
          </div>
        )}

        {view === 'questions' && !actionLoading && (
          <div className="space-y-2 max-h-[320px] overflow-y-auto">
            {questions.map((faq) => (
              <button
                key={faq.id}
                onClick={() => handleQuestionClick(faq)}
                className="w-full text-left px-3 py-2 rounded-lg text-sm transition-colors hover:bg-gray-100"
                style={{
                  backgroundColor: '#fff',
                  color: '#333',
                  border: '1px solid #e0e0e0',
                  cursor: 'pointer',
                }}
              >
                {faq.question}
              </button>
            ))}
            {user && (
              <button
                onClick={() => handleQuestionClick({ id: 'other-query', question: 'Requiero más información', isOtherQuery: true })}
                className="w-full text-left px-3 py-2 rounded-lg text-sm transition-colors hover:opacity-90"
                style={{
                  backgroundColor: '#FFF3E0',
                  color: '#E65100',
                  border: '1px solid #FFB74D',
                  cursor: 'pointer',
                  fontWeight: 500,
                }}
              >
                Requiero más información
              </button>
            )}
            <button
              onClick={handleBackToCategories}
              className="w-full text-center px-3 py-2 rounded-lg text-sm font-medium transition-colors hover:opacity-90 mt-3"
              style={{
                backgroundColor: '#09274A',
                color: '#fff',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              ⬅ Volver a categorías
            </button>
          </div>
        )}

        {view === 'answer' && !actionLoading && (
          <>
            {user && selectedFaqId && (
              <StarRating key={ratingKey} faqId={selectedFaqId} />
            )}
            <div className="flex gap-2">
              <button
                onClick={handleBackToQuestions}
                className="flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors hover:opacity-90"
                style={{
                  backgroundColor: '#09274A',
                  color: '#fff',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                ⬅ Volver
              </button>
              <button
                onClick={handleOtherQuestion}
                className="flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors hover:opacity-90"
                style={{
                  backgroundColor: '#C42251',
                  color: '#fff',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                ✋ Otra duda
              </button>
            </div>
          </>
        )}

        {view === 'loading' && (
          <div className="flex justify-center py-8">
            <CircularProgress size={24} style={{ color: '#0488AF' }} />
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

    </Paper>
  );
}
